import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

export interface ExportConfig {
  data: any[];
  filename?: string;
  sheetName?: string;
  columnWidths?: number[];
}

export interface ExportResult {
  success: boolean;
  filename?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  /**
   * Export data to Excel format
   */
  async exportToExcel(config: ExportConfig): Promise<ExportResult> {
    try {
      if (!config.data || config.data.length === 0) {
        return { success: false, error: 'No data available to export' };
      }

      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(config.data);

      // Apply column widths if provided
      if (config.columnWidths && config.columnWidths.length > 0) {
        ws['!cols'] = config.columnWidths.map(width => ({ wch: width }));
      } else {
        // Auto-size columns based on content
        ws['!cols'] = this.autoSizeColumns(config.data);
      }

      const sheetName = config.sheetName || 'Data';
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const filename = this.generateFilename(config.filename, 'xlsx');
      XLSX.writeFile(wb, filename);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Excel export error:', error);
      return { success: false, error: 'Failed to export to Excel' };
    }
  }

  /**
   * Format number with thousand separators
   */
  formatNumber(num: number | string): string {
    if (num === null || num === undefined) return 'N/A';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    return isNaN(numValue) ? 'N/A' : new Intl.NumberFormat('en-US').format(numValue);
  }

  /**
   * Auto-size columns based on content
   */
  private autoSizeColumns(data: any[]): { wch: number }[] {
    if (!data || data.length === 0) return [];
    
    const keys = Object.keys(data[0]);
    return keys.map(key => {
      // Calculate max length for each column
      let maxLength = key.length; // Header length
      
      data.forEach(row => {
        const cellValue = String(row[key] || '');
        maxLength = Math.max(maxLength, cellValue.length);
      });
      
      // Add some padding and set reasonable limits
      const width = Math.min(Math.max(maxLength + 2, 10), 50);
      return { wch: width };
    });
  }

  /**
   * Generate filename with timestamp
   */
  private generateFilename(customFilename?: string, extension: string = 'xlsx'): string {
    if (customFilename) {
      // Ensure the filename has the correct extension
      const nameWithoutExt = customFilename.replace(/\.[^/.]+$/, '');
      return `${nameWithoutExt}.${extension}`;
    }
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    return `export_${timestamp}.${extension}`;
  }
} 